'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';

interface Props {
    pageIndex: number;
    setPageIndex: React.Dispatch<React.SetStateAction<number>>;
    pageSize: number;
}

// pageIndex 為 0-based（對齊 API），畫面顯示與網址參數則一律用 1-based
const Pagination = ({ pageIndex = 0, setPageIndex, pageSize = 0 }: Props) => {
    const [inputIndex, setInputIndex] = useState<string>(String(pageIndex + 1));

    const updateSearchParams = (newPageIndex: number) => {
        const displayPage = newPageIndex + 1;
        const params = new URLSearchParams(window.location.search);

        if (displayPage <= 1) params.delete('page');
        else params.set('page', displayPage.toString());

        const newUrl = `${window.location.pathname}${
            params.toString() ? `?${params.toString()}` : ''
        }`;

        window.scrollTo({ top: 0 });

        // 更新 URL
        window.history.replaceState({}, '', newUrl);
    };

    const handleFinalUpdate = (target: number) => {
        let safePage = target;
        if (target < 0) safePage = 0;
        if (target > pageSize - 1) safePage = pageSize - 1;

        setPageIndex(safePage);
        setInputIndex(String(safePage + 1));
        updateSearchParams(safePage);
    };

    const onSelectPage = (delta: number) => {
        const next = pageIndex + delta;
        if (next >= 0 && next <= pageSize - 1) {
            handleFinalUpdate(next);
        }
    };

    const onPageChange = (value: string) => {
        if (/^\d*$/.test(value)) {
            setInputIndex(value);
        }
    };

    const onSubmit = () => {
        const num = parseInt(inputIndex, 10);
        const target = num - 1;
        if (target === pageIndex) return;

        if (isNaN(num) || num < 1) {
            handleFinalUpdate(0);
        } else {
            handleFinalUpdate(target);
        }
    };

    const handleKeyDown = (e: any) => {
        if (e.key === 'Enter') {
            onSubmit();
            e.target.blur();
        }
    };

    useEffect(() => {
        setInputIndex(String(pageIndex + 1));
    }, [pageIndex]);

    if (pageSize < 2) return null;

    return (
        <div className="w-full my-10 center-center lg:justify-between">
            {/* 回到第一頁按鈕 */}
            <button
                onClick={() => handleFinalUpdate(0)}
                className={`px-5 py-3 hidden items-center gap-x-2 rounded-md ${
                    pageIndex === 0 ? 'cursor-default' : 'trans hover:bg-purple9 group'
                } lg:flex`}
            >
                <span
                    className={`w-6 aspect-square bg-contain bg-center shrink-0
                        ${pageIndex === 0 ? '' : 'trans group-hover:brightness-400'}
                        bg-[url("/images/icon/icon_first.svg")] dark:bg-[url("/images/icon/icon_dark_first.svg")]`}
                />
                <span
                    className={`text-btn1 text-vPurple ${
                        pageIndex === 0 ? '' : 'trans group-hover:text-[#fff]'
                    }`}
                >
                    Go to page 1
                </span>
            </button>

            <div className="flex gap-x-3.25 [&>button]:px-5.5 [&>button]:py-3 [&>button]:rounded-md">
                {/* 上一頁 */}
                <button
                    onClick={() => onSelectPage(-1)}
                    className={`border-vPurple group border trans relative ${
                        pageIndex <= 0 ? 'cursor-default' : 'hover:bg-vPurple '
                    }`}
                >
                    <span
                        className={`w-6 aspect-square bg-contain bg-center trans set-center
                        ${pageIndex <= 0 ? '' : 'group-hover:brightness-400'}
                        bg-[url("/images/icon/icon_previous.svg")] dark:bg-[url("/images/icon/icon_dark_previous.svg")]`}
                    />
                </button>

                {/* 下一頁 */}
                <button
                    onClick={() => onSelectPage(1)}
                    className={`bg-vPurple group text-btn1 text-[#fff] flex trans gap-x-2 items-center
                        ${pageIndex === pageSize - 1 ? 'cursor-default' : 'hover:bg-purple'}`}
                >
                    <span>Next Page</span>
                    <Image
                        className="brightness-300"
                        width={24}
                        height={24}
                        src="/images/icon/icon_next.svg"
                        alt="next"
                    />
                </button>
            </div>

            {/* 頁碼輸入 */}
            <div className="hidden items-center text-btn1 text-black5 gap-x-3 lg:flex">
                Page{' '}
                <input
                    className="rounded-md border border-black2 bg-white py-2 w-13 text-center text-black5"
                    type="text"
                    value={inputIndex}
                    onChange={(e) => onPageChange(e.target.value)}
                    onBlur={onSubmit}
                    onKeyDown={handleKeyDown}
                    aria-label="page"
                />{' '}
                of {pageSize}
            </div>
        </div>
    );
};

export default Pagination;
